import React, { useContext, useState, useEffect } from 'react'
import { Form, Input, Button, Modal, Radio, Alert } from 'antd'
import './index.css'
import client from 'apollo'
import { CREATE_REVIEW_FEEDBACK_FORM } from './graphql/Mutation'
import { AppContext } from 'AppContext'
import moment from 'moment'
import FinLogo from '../../assets/images/logo-tagline-new.png'
import openNotification from 'utils/Notification'
import { canUserAdd, canUserSave } from 'utils/User'

const StarIcon = ({ filled }) => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 24 24"
    fill={filled ? '#fadb14' : 'none'}
    stroke={filled ? '#fadb14' : '#d9d9d9'}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'block' }}
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
)

const StarRatingField = ({ value = 0, onChange }) => {
  const [hoverVal, setHoverVal] = useState(0)

  return (
    <div className='star-rating-container'>
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= (hoverVal || value)
        return (
          <div
            key={star}
            className={`star-item ${isFilled ? 'filled' : ''}`}
            onMouseEnter={() => setHoverVal(star)}
            onMouseLeave={() => setHoverVal(0)}
            onClick={() => onChange && onChange(star)}
          >
            <span className='star-number'>{star}</span>
            <div className='star-icon-wrapper'>
              <StarIcon filled={isFilled} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

const ReviewFeedBackForm = () => {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const {
    state: { currentUser, userPermissions, isClient, isAudit } = {},
  } = useContext(AppContext) || {}

  const effectiveUser = userPermissions || currentUser
  const canAdd = canUserAdd(effectiveUser, isClient)
  const canSave = canUserSave(effectiveUser, isClient)
  const canSubmitFeedback = isClient || (!isAudit && canAdd && canSave)

  const isUserLoggedIn = !!(
    currentUser &&
    (currentUser.email || currentUser.clientCode || currentUser.userName)
  )

  useEffect(() => {
    if (currentUser) {
      const name =
        currentUser.firstName || currentUser.lastName
          ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
          : currentUser.clientName || currentUser.userName || ''
      form.setFieldsValue({
        clientName: name,
        email: currentUser.email || '',
        phone: currentUser.phone || currentUser.mobile || '',
      })
    }
  }, [currentUser, form])

  const SubmitResponse = (values) => {
    if (!canSubmitFeedback) {
      openNotification('error', 'You do not have permission to submit feedback.')
      return
    }
    setSubmitting(true)
    const responsePayload = {
      fees_value: values.q1_fees,
      association_3_years: values.q2_association,
      investment_strategy: values.q3_strategy,
      advisory_recommendations: values.q4_recommendations,
      ilts_execution: values.q5_ilts,
      backoffice_ease: values.q6_backoffice,
      reports_understanding: values.q7_reports,
      services_experience: {
        client_onboarding: values.q8_client_onboarding,
        emandate_fee_collection: values.q8_emandate_fee_collection,
        advisory_service: values.q8_advisory_service,
        backoffice_portal: values.q8_backoffice_portal,
        customer_support: values.q8_customer_support,
        portfolio_reports: values.q8_portfolio_reports,
      },
      recommend_likelihood: values.q9_recommend,
      appreciated_aspect: values.q10_appreciate,
      improvement_area: values.q11_improvement,
    }

    client
      .mutate({
        mutation: CREATE_REVIEW_FEEDBACK_FORM,
        variables: {
          data: {
            ClientCode: currentUser?.clientCode || '',
            UserName: currentUser?.userName || '',
            Email: currentUser?.email || values.email || '',
            clientName: values.clientName || '',
            phone: values.phone || '',
            SubmitDate: moment().format('YYYY-MM-DD'),
            Response: JSON.stringify(responsePayload),
            Suggestion: values.q11_improvement || values.q10_appreciate || '',
          },
        },
      })
      .then(() => {
        localStorage.setItem('hasRedirectedToReviewForm', 'true')
        Modal.success({
          title: 'Thank You!',
          content: 'Your feedback has been successfully submitted.',
          centered: true,
          onOk: () => {
            Modal.destroyAll()
            window.location.href = '/'
          },
        })
      })
      .catch((err) => {
        console.error('Feedback submission error:', err)
        Modal.error({
          title: 'Submission Failed',
          content:
            'Something went wrong while submitting your feedback. Please try again.',
          centered: true,
        })
      })
      .finally(() => setSubmitting(false))
  }

  const matrixServices = [
    { key: 'q8_client_onboarding', label: 'Client Onboarding' },
    { key: 'q8_emandate_fee_collection', label: 'E-Mandate / Fee Collection' },
    { key: 'q8_advisory_service', label: 'Advisory Service' },
    { key: 'q8_backoffice_portal', label: 'Back Office Portal' },
    { key: 'q8_customer_support', label: 'Customer Support' },
    { key: 'q8_portfolio_reports', label: 'Portfolio Reports' },
  ]

  const matrixOptions = [
    'Very Poor',
    'Needs Improvement',
    'Average',
    'Good',
    'Excellent',
  ]

  return (
    <div className='review-form-container'>
      <div className='review-form-card'>
        {!canSubmitFeedback && (
          <div style={{ marginBottom: 20 }}>
            <Alert
              message='Read-Only Access: You do not have permission to submit feedback.'
              type='warning'
              showIcon
            />
          </div>
        )}
        <div className='review-form-header'>
          <img src={FinLogo} alt='Finideas Logo' />
        </div>

        <Form
          form={form}
          className='review-form'
          layout='vertical'
          onFinish={SubmitResponse}
          requiredMark={false}
        >
          {/* Contact Details (Auto-filled & disabled if logged in, fillable if standalone /review-form) */}
          <div className='review-form-contact-row'>
            <div className='contact-row-flex'>
              <Form.Item
                name='clientName'
                label={
                  <span className='field-label'>
                    Name <span style={{ color: '#ff4d4f' }}>*</span>
                  </span>
                }
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input disabled={isUserLoggedIn} size='large' />
              </Form.Item>

              <Form.Item
                name='phone'
                label={
                  <span className='field-label'>
                    Mobile Number <span style={{ color: '#ff4d4f' }}>*</span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter your mobile number' },
                ]}
              >
                <Input disabled={isUserLoggedIn} size='large' />
              </Form.Item>
            </div>

            <Form.Item
              name='email'
              label={
                <span className='field-label'>
                  Email id <span style={{ color: '#ff4d4f' }}>*</span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input disabled={isUserLoggedIn} size='large' />
            </Form.Item>
          </div>

          {/* Question 1 */}
          <div className='question-item'>
            <div className='question-text'>
              1. I believe the fees I pay provide good value for the services and guidance I receive.
            </div>
            <Form.Item
              name='q1_fees'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 2 */}
          <div className='question-item'>
            <div className='question-text'>
              2. I am comfortable continuing my association with Finideas over the next 3 years.
            </div>
            <Form.Item
              name='q2_association'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 3 */}
          <div className='question-item'>
            <div className='question-text'>
              3. I understand the purpose and benefits of my current investment strategy.<span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q3_strategy'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 4 */}
          <div className='question-item'>
            <div className='question-text'>
              4. The advisory recommendations are easy to understand.<span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q4_recommendations'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 5 */}
          <div className='question-item'>
            <div className='question-text'>
              5. I am comfortable with the current process of ILTS advisory execution.<span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q5_ilts'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 6 */}
          <div className='question-item'>
            <div className='question-text'>
              6. The Finideas Back Office is easy to use.<span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q6_backoffice'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 7 */}
          <div className='question-item'>
            <div className='question-text'>
              7. Investment reports and portfolio performance are easy to understand.<span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q7_reports'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 8 - Matrix Table */}
          <div className='question-item'>
            <div className='question-text'>
              8. Rate your experience with the following services:<span style={{ color: 'red' }}>*</span>
            </div>
            <div className='matrix-table-container'>
              <table className='matrix-table'>
                <thead>
                  <tr>
                    <th className='service-col-header'></th>
                    {matrixOptions.map((opt) => (
                      <th key={opt}>{opt}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixServices.map((service) => (
                    <tr key={service.key}>
                      <td className='service-name'>{service.label}</td>
                      {matrixOptions.map((opt) => (
                        <td key={opt}>
                          <Form.Item
                            name={service.key}
                            style={{ margin: 0 }}
                            rules={[
                              { required: true, message: 'Select an option' },
                            ]}
                          >
                            <Radio.Group>
                              <Radio value={opt} />
                            </Radio.Group>
                          </Form.Item>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Question 9 */}
          <div className='question-item'>
            <div className='question-text'>
              9. How likely are you to recommend Finideas to your friends or family?<span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q9_recommend'
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <StarRatingField />
            </Form.Item>
          </div>

          {/* Question 10 */}
          <div className='question-item'>
            <div className='question-text'>
              10. What is the one thing you appreciate most about Finideas? <span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q10_appreciate'
              rules={[
                { required: true, message: 'Please enter your response' },
              ]}
            >
              <Input.TextArea
                rows={2}
                placeholder='Your answer'
                className='answer-input-line'
              />
            </Form.Item>
          </div>

          {/* Question 11 */}
          <div className='question-item'>
            <div className='question-text'>
              11. What is one area where you feel we can improve?<span style={{ color: 'red' }}>*</span>
            </div>
            <Form.Item
              name='q11_improvement'
              rules={[
                { required: true, message: 'Please enter your response' },
              ]}
            >
              <Input.TextArea
                rows={2}
                placeholder='Your answer'
                className='answer-input-line'
              />
            </Form.Item>
          </div>

          {/* Submit Button */}
          <div className='submit-btn-wrapper'>
            <Button
              type='primary'
              htmlType='submit'
              loading={submitting}
              className='review-submit-btn'
              disabled={!canSubmitFeedback}
            >
              Submit
            </Button>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default ReviewFeedBackForm
